import {
  CLIENT_ERROR_LIMITS,
  scrubErrorText,
  type ClientErrorItem,
  type ClientErrorReport,
  type ClientFailureCounter,
} from '@twinden/shared';

// First-party error reporting (M67, docs/monitoring.md D254): crashes and
// errors go to Twinden's own server, scrubbed of anything that could name a
// person, a device or a session, deduplicated per session and rate-limited.
// Counters for sends, uploads and call set-up ride in the same batch.
// Nothing goes to a third party.

const commit = typeof __BUILD_COMMIT__ === 'string' ? __BUILD_COMMIT__ : 'dev';
/** `web@<short commit>`; `web@dev` locally. */
export const APP_VERSION = `web@${commit.slice(0, 7)}`;

const ENDPOINT = '/api/client-errors';

export type SendReport = (body: ClientErrorReport, final: boolean) => void;

interface State {
  enabled: boolean;
  send: SendReport;
  now: () => number;
  errors: Array<Extract<ClientErrorItem, { kind: 'error' }>>;
  counters: Map<ClientFailureCounter, number>;
  seen: Set<string>;
  recent: number[];
  timer: ReturnType<typeof setTimeout> | null;
}

function defaultSend(body: ClientErrorReport, final: boolean) {
  const json = JSON.stringify(body);
  // Leaving the page: a beacon outlives it; otherwise fetch, kept alive.
  if (final && typeof navigator.sendBeacon === 'function') {
    try {
      if (
        navigator.sendBeacon(
          ENDPOINT,
          new Blob([json], { type: 'application/json' })
        )
      )
        return;
    } catch {
      // Fall through to fetch.
    }
  }
  void fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: json,
    keepalive: true,
  }).catch(() => undefined);
}

function defaultEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (import.meta.env?.MODE === 'test') return false;
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(
    window.location.hostname
  );
  return !local || import.meta.env?.VITE_REPORT_ERRORS === 'true';
}

const state: State = {
  enabled: defaultEnabled(),
  send: defaultSend,
  now: () => Date.now(),
  errors: [],
  counters: new Map(),
  seen: new Set(),
  recent: [],
  timer: null,
};

/** Tests: swap the transport and clock, and switch reporting on. */
export function configureErrorReporting(options: {
  enabled?: boolean;
  send?: SendReport;
  now?: () => number;
}) {
  if (options.enabled !== undefined) state.enabled = options.enabled;
  if (options.send) state.send = options.send;
  if (options.now) state.now = options.now;
}

/** Tests: forget everything queued and seen. */
export function resetErrorReporting() {
  state.errors = [];
  state.counters = new Map();
  state.seen = new Set();
  state.recent = [];
  if (state.timer) clearTimeout(state.timer);
  state.timer = null;
}

/** A small, stable hash: the dedupe key, never sent. */
function hash(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i += 1)
    h = ((h * 33) ^ text.charCodeAt(i)) >>> 0;
  return h.toString(16);
}

const firstLine = (text: string) => text.split('\n')[0] ?? '';
const topFrame = (stack: string | undefined) =>
  (stack ?? '')
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line.startsWith('at ') || line.includes('@')) ?? '';

/** The current screen: the path with ids scrubbed, never the query string. */
export function currentRoute(): string {
  if (typeof window === 'undefined') return '';
  return scrubErrorText(window.location.pathname).slice(
    0,
    CLIENT_ERROR_LIMITS.ROUTE
  );
}

export interface ErrorInput {
  message: string;
  stack?: string | null;
  route?: string;
}

/**
 * What an error object gives us. Anything else (a rejected record, a
 * string, an object) is described by its kind only: the app holds
 * decrypted content in memory, and none of it may travel in a report.
 */
export function describeError(value: unknown): {
  message: string;
  stack?: string;
} {
  if (value instanceof Error) {
    return {
      message: value.message || value.name,
      stack: value.stack ?? undefined,
    };
  }
  const kind =
    value !== null && typeof value === 'object'
      ? (value.constructor?.name ?? 'Object')
      : typeof value;
  return { message: `Non-Error value: ${kind}` };
}

function schedule(final = false) {
  if (final) {
    if (state.timer) clearTimeout(state.timer);
    state.timer = null;
    flushErrorReports(true);
    return;
  }
  if (state.timer) return;
  state.timer = setTimeout(() => {
    state.timer = null;
    flushErrorReports(false);
  }, CLIENT_ERROR_LIMITS.BATCH_EVERY_MS);
}

/**
 * Queues one error (D254): scrubbed, deduplicated for this session, at
 * most 5 a minute. Returns whether it was queued.
 */
export function reportError(input: ErrorInput): boolean {
  if (!state.enabled) return false;
  const message = scrubErrorText(input.message.trim() || 'Error').slice(
    0,
    CLIENT_ERROR_LIMITS.MESSAGE
  );
  const stack = input.stack
    ? scrubErrorText(input.stack).slice(0, CLIENT_ERROR_LIMITS.STACK)
    : undefined;
  const route = (input.route ?? currentRoute()).slice(
    0,
    CLIENT_ERROR_LIMITS.ROUTE
  );
  const key = hash(`${route}|${firstLine(message)}|${topFrame(stack)}`);
  if (state.seen.has(key)) return false;
  const now = state.now();
  state.recent = state.recent.filter((at) => now - at < 60_000);
  if (state.recent.length >= CLIENT_ERROR_LIMITS.ERRORS_PER_MINUTE)
    return false;
  state.recent.push(now);
  state.seen.add(key);
  state.errors.push({
    kind: 'error',
    message,
    ...(stack ? { stack } : {}),
    ...(route ? { route } : {}),
  });
  schedule();
  return true;
}

/** Counts a failure the code already knows about; no detail travels with it. */
export function countClientFailure(name: ClientFailureCounter, count = 1) {
  if (!state.enabled) return;
  state.counters.set(name, (state.counters.get(name) ?? 0) + count);
  schedule();
}

/** Sends what is queued, if anything. `final` means the page is going away. */
export function flushErrorReports(final = false) {
  if (!state.enabled) return;
  const items: ClientErrorItem[] = [...state.errors];
  for (const [name, count] of state.counters) {
    items.push({ kind: 'counter', name, count: Math.min(count, 1000) });
  }
  state.errors = [];
  state.counters = new Map();
  if (items.length === 0) return;
  const body: ClientErrorReport = {
    platform: 'web',
    appVersion: APP_VERSION,
    items: items.slice(0, CLIENT_ERROR_LIMITS.ITEMS),
  };
  // The server takes 32 KB: drop the oldest errors' stacks, then the
  // errors themselves, until the batch fits (counters always ride).
  // Measured in bytes: a Devanagari stack is three bytes a character.
  const encoder = new TextEncoder();
  while (
    encoder.encode(JSON.stringify(body)).length >
    CLIENT_ERROR_LIMITS.BATCH_BYTES
  ) {
    const withStack = body.items.find(
      (item) => item.kind === 'error' && item.stack
    );
    if (withStack && withStack.kind === 'error') {
      delete withStack.stack;
      continue;
    }
    const index = body.items.findIndex((item) => item.kind === 'error');
    if (index < 0) break;
    body.items.splice(index, 1);
  }
  state.send(body, final);
}

let installed = false;

/** Installs the window handlers once (main.tsx). */
export function setupErrorReporting() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (event) => {
    const described = describeError(event.error ?? event.message);
    reportError({
      message: described.message,
      stack:
        described.stack ??
        (event.filename
          ? `at ${event.filename}:${event.lineno}:${event.colno}`
          : undefined),
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const described = describeError(event.reason);
    reportError({
      message: `Unhandled rejection: ${described.message}`,
      stack: described.stack,
    });
  });
  window.addEventListener('pagehide', () => schedule(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') schedule(true);
  });
}
